import * as React from "react";
import {
    Alert,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Switch,
} from "@jhool-io/fe-components";
import { useParams, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useFetchRemitClaims } from "../../hooks/remits.queries";
import RemitBatchView from "../RemitBatchView/RemitBatchView";
import ListState from "../../../../components/ListState/ListState";
import {
    cn,
    formatDate,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { groupRemitsIntoTabs } from "../../helpers/helpers.remits";
import ArchiveRemitForm from "../ArchiveRemitForm/ArchiveRemitForm";
import MoveRemitForm from "../MoveRemitForm/MoveRemitForm";
import { useArchiveRemit } from "../../hooks/remits.mutations";
import useToast from "../../../../hooks/useToast";
import Loader from "../../../../shared-ui/Loader/Loader";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import { useGetInvoicePayment } from "../../../billing/hooks/queries/billing.queries";

interface SwitchOptions {
    title: React.ReactNode;
    key: string;
    onClick: () => void;
}

type DialogTypes = "archive" | "unarchive" | "move";

export default function RemitsWrapper() {
    const [dialogInView, setDialogInView] = React.useState<DialogTypes | null>(
        null
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const { noteId } = useParams();

    const showArchivedRemits = searchParams.get("archived_remits") || "false";

    const { data, isLoading, error, isSuccess } = useFetchRemitClaims({
        note_id: noteId,
    });

    const remitsInvoiceId = data?.data?.[0]?.invoice_id ?? "";

    // get payment invoice data
    const {
        data: invoiceData,
        isError: invoiceDataError,
        isLoading: invoiceDataLoading,
    } = useGetInvoicePayment(
        remitsInvoiceId,
        { invoice_id: remitsInvoiceId },
        Boolean(remitsInvoiceId)
    );

    const insBilledAmount = invoiceData?.ledger?.insurance_amount || 0;
    const insPaidAmount = invoiceData?.ledger?.insurance_paid || 0;
    const ptResponsibilityAmount =
        invoiceData?.ledger?.patient_responsibility || 0;
    const pr100Amount = invoiceData?.ledger?.pr100_collected || 0;
    const hasInsuranceActivity = insBilledAmount > 0 || insPaidAmount > 0;
    const writeOffAmount = hasInsuranceActivity
        ? invoiceData?.ledger?.insurance_writeoff ?? 0
        : invoiceData?.ledger?.coins_written_off ?? 0;
    const ptPaidAmount = invoiceData?.ledger?.coins_collected || 0;
    const balanceAmount = invoiceData?.ledger?.balance_due || 0;

    const balanceAmountDisplay = () => {
        if (balanceAmount === 0) {
            return (
                <span className="font-semibold">{showMoneyInAppFormat(0)}</span>
            );
        }
        if (balanceAmount > 0) {
            return (
                <span className="font-semibold text-primary">
                    {showMoneyInAppFormat(balanceAmount)}
                </span>
            );
        }

        return (
            <span className="font-semibold text-danger">
                {`(${showMoneyInAppFormat(Math.abs(balanceAmount))})`}
            </span>
        );
    };

    const archiveMutation = useArchiveRemit();

    const archiveRemitIsMutating = useIsMutating(["archive-remit"]);

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const handleSwitchButtonClick = (remit: string) => {
        searchParams.set("remit_in_view", remit);
        setSearchParams(searchParams);
    };

    let switchOptions: SwitchOptions[] = [];
    let activeSwitch: string = searchParams.get("remit_in_view") || "";

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return (
            <ListState
                isError
                stateHelperText="Try reloading this page to solve this issue"
                errorMsg={
                    error?.response?.data.message ||
                    "Cannot display remit details at this time. Please try again later"
                }
            />
        );
    }

    if (data && data.data && isSuccess) {
        if (data.data.length === 0) {
            return (
                <ListState
                    isError={false}
                    stateHelperText="No remit to display"
                    emptyMessage="No remit to display"
                />
            );
        }
        if (data.data.length >= 1) {
            const archivedFilterText = () => {
                if (showArchivedRemits === "true") return "Archived";
                return "Unarchived";
            };

            const filteredRemitsList = showArchivedRemits
                ? data.data.filter(
                      (remit) =>
                          String(remit.is_archived) === showArchivedRemits
                  )
                : data.data;

            const tabs = groupRemitsIntoTabs(filteredRemitsList);

            switchOptions = tabs.map((tabRemits, index) => {
                const head = tabRemits[0];
                return {
                    title: `${
                        head.remit_received_date
                            ? formatDate(
                                  head.remit_received_date.replace(
                                      "+00:00",
                                      ""
                                  ),
                                  true,
                                  "MM/dd/yyyy"
                              )
                            : "--"
                    } #${index + 1}`,
                    key: head.remittance_id,
                    onClick: () => handleSwitchButtonClick(head.remittance_id),
                };
            });

            const activeTab =
                tabs.find((tabRemits) =>
                    tabRemits.some((r) => r.remittance_id === activeSwitch)
                ) || tabs[0];

            if (activeTab) {
                activeSwitch = activeTab[0].remittance_id;
            }

            const handleSetArchivedFilter = (archiveStatus: string) => {
                searchParams.set("archived_remits", archiveStatus);

                const firstMatchingRemit = data.data.find(
                    (remit) => String(remit.is_archived) === archiveStatus
                );
                if (firstMatchingRemit) {
                    searchParams.set(
                        "remit_in_view",
                        firstMatchingRemit.remittance_id
                    );
                } else searchParams.delete("remit_in_view");

                setSearchParams(searchParams);
            };

            const batchInView = activeTab ?? [];
            const remitInView = batchInView[0];

            const handleUnArchive = () => {
                const dataToSend = {
                    archive_status: false,
                    remit_ids_to_archive: batchInView.map(
                        (r) => r.remittance_id
                    ),
                };

                archiveMutation.mutate(dataToSend, {
                    onSuccess: async (res) => {
                        await queryClient.invalidateQueries({
                            queryKey: [`remit-claims`],
                        });

                        searchParams.delete("remit_in_view");
                        setSearchParams(searchParams);
                        setDialogInView(null);

                        toast({
                            mode: "success",
                            message:
                                res.message || "Remit archive status updated",
                        });
                    },
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Could not update archive status at this time",
                        });
                    },
                });
            };

            return (
                <>
                    {remitInView &&
                        (dialogInView === "archive" ||
                            dialogInView === "unarchive") && (
                            <Dialog open={Boolean(dialogInView)}>
                                <DialogContent
                                    handleCloseDialog={() =>
                                        setDialogInView(null)
                                    }
                                    variant="center"
                                    title={
                                        remitInView?.is_archived
                                            ? "Unarchive Remit"
                                            : "Archive Remit"
                                    }
                                    saveText={
                                        remitInView?.is_archived
                                            ? "Unarchive Remit"
                                            : "Archive Remit"
                                    }
                                    submitBtnFormValue="archive-remit"
                                    isSubmitBtnDisabled={
                                        archiveRemitIsMutating > 0
                                    }
                                    isCancelBtnDisabled={
                                        archiveRemitIsMutating > 0
                                    }
                                    onSaveClick={
                                        dialogInView === "unarchive"
                                            ? handleUnArchive
                                            : undefined
                                    }
                                >
                                    {dialogInView === "unarchive" ? (
                                        <div>
                                            <h3 className="text-title font-semibold text-text mb-3">
                                                Unarchive remit
                                            </h3>
                                            <p className="text-base font-normal text-textSec">
                                                Are you sure you want to
                                                unarchive this remit?
                                            </p>
                                        </div>
                                    ) : (
                                        <ArchiveRemitForm
                                            onFormSubmit={() =>
                                                setDialogInView(null)
                                            }
                                            remitIds={batchInView.map(
                                                (r) => r.remittance_id
                                            )}
                                            isArchived={Boolean(
                                                remitInView.is_archived
                                            )}
                                        />
                                    )}
                                </DialogContent>
                            </Dialog>
                        )}
                    {remitInView && dialogInView === "move" && (
                        <Dialog open>
                            <DialogContent
                                handleCloseDialog={() => setDialogInView(null)}
                                variant="center"
                                title="Move remit"
                                wrapperClass="w-[850px]"
                                classNames="px-0"
                                showFooter={false}
                            >
                                <MoveRemitForm
                                    remits={batchInView}
                                    onFormSubmit={() => setDialogInView(null)}
                                />
                            </DialogContent>
                        </Dialog>
                    )}
                    <Card
                        classNames={cn(
                            "mb-[--spacing-header] gap-[--spacing-items]"
                        )}
                    >
                        <div className="flex justify-between">
                            {invoiceDataError ? (
                                <div className="text-danger flex items-center gap-x-1 [&_svg]:size-3.5">
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line
                                            x1="12"
                                            y1="16"
                                            x2="12.01"
                                            y2="16"
                                        />
                                    </svg>
                                    <span>
                                        Could not load invoice balance breakdown
                                    </span>
                                </div>
                            ) : null}
                            {invoiceDataLoading ? (
                                <div className="flex items-center">
                                    <Loader loaderType="inline" />
                                </div>
                            ) : null}
                            {invoiceData ? (
                                <div className="flex items-start gap-x-4">
                                    <div className="flex flex-col items-start gap-y-1">
                                        <span className="text-textSec text-caption font-medium uppercase">
                                            INS Billed
                                        </span>
                                        <span className="font-semibold">
                                            {showMoneyInAppFormat(
                                                insBilledAmount
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-start gap-y-1">
                                        <span className="text-textSec text-caption font-medium uppercase">
                                            INS PAID
                                        </span>
                                        <span className="font-semibold">
                                            {showMoneyInAppFormat(
                                                insPaidAmount
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-start gap-y-1">
                                        <span className="text-textSec text-caption font-medium uppercase">
                                            PT RESPONSIBILITY
                                        </span>
                                        <span className="font-semibold">
                                            {showMoneyInAppFormat(
                                                ptResponsibilityAmount
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-start gap-y-1">
                                        <span className="text-textSec text-caption font-medium uppercase">
                                            PT PAID
                                        </span>
                                        <span className="font-semibold">
                                            {showMoneyInAppFormat(ptPaidAmount)}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-start gap-y-1">
                                        <span className="text-textSec text-caption font-medium uppercase">
                                            PR 100
                                        </span>
                                        <span className="font-semibold">
                                            {showMoneyInAppFormat(pr100Amount)}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-start gap-y-1">
                                        <span className="text-textSec text-caption font-medium uppercase">
                                            WRITE-OFF
                                        </span>
                                        <span className="font-semibold">
                                            {showMoneyInAppFormat(
                                                writeOffAmount
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-start gap-y-1">
                                        <span
                                            className={cn(
                                                "text-caption font-medium uppercase text-textSec"
                                            )}
                                        >
                                            BALANCE
                                        </span>
                                        {balanceAmountDisplay()}
                                    </div>
                                </div>
                            ) : null}
                            <div className="flex items-center gap-x-[--spacing-items]">
                                {filteredRemitsList.length > 0 && (
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <Button
                                                aria-label="remit-action-button"
                                                variant="primary"
                                                type="button"
                                                iconRight={<ChevronDownIcon />}
                                            >
                                                Actions
                                            </Button>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            width="auto"
                                            align="end"
                                        >
                                            <DropdownItem
                                                onClick={() =>
                                                    setDialogInView(
                                                        remitInView?.is_archived
                                                            ? "unarchive"
                                                            : "archive"
                                                    )
                                                }
                                            >
                                                {remitInView?.is_archived
                                                    ? "Unarchive"
                                                    : "Archive"}
                                            </DropdownItem>
                                            {!remitInView?.is_archived && (
                                                <DropdownItem
                                                    onClick={() =>
                                                        setDialogInView("move")
                                                    }
                                                >
                                                    Move remit
                                                </DropdownItem>
                                            )}
                                        </DropdownContent>
                                    </Dropdown>
                                )}
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {archivedFilterText()}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end">
                                        <DropdownItem
                                            isSelected={
                                                showArchivedRemits === "true"
                                            }
                                            onClick={() =>
                                                handleSetArchivedFilter("true")
                                            }
                                        >
                                            Archived
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedRemits === "false"
                                            }
                                            onClick={() =>
                                                handleSetArchivedFilter("false")
                                            }
                                        >
                                            Unarchived
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        </div>
                    </Card>
                    {filteredRemitsList.length > 0 && (
                        <div className="mb-[--spacing-header]">
                            <Switch
                                switchOptions={switchOptions}
                                activeSwitch={activeSwitch}
                                size="large"
                            />
                        </div>
                    )}
                    {remitInView?.is_archived && remitInView.archived_notes && (
                        <div>
                            <Alert
                                type="info"
                                description={remitInView.archived_notes}
                                classNames="mb-[--spacing-items]"
                            />
                        </div>
                    )}

                    {batchInView.length > 0 && (
                        <RemitBatchView remits={batchInView} />
                    )}
                    {filteredRemitsList.length === 0 && (
                        <ListState
                            isError={false}
                            stateHelperText="No remit matched applied filter"
                            emptyMessage="No remit to display"
                        />
                    )}
                </>
            );
        }
    }

    return null;
}
